import type { OnboardingApplicationHydrated } from '../../../lib/flotiq-client/types/api-response';

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getEnv } from '../../../lib/env';
import { getTestProps, getTranslatedField } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import Card from '../../../components/Card/Card';
import LinkButton from '../../../components/LinkButton/LinkButton';

import { useModals } from '../../../contexts/ModalContext';

import { EyeIcon } from '../../../images/shapes';

import PopulateSpaceTemplateDetailsContent from './PopulateSpaceTemplateDetailsContent';

const PopulateSpaceAppCard = ({
  application,
  onApplicationSelect,
  testId,
}: {
  application: OnboardingApplicationHydrated;
  onApplicationSelect: (application: OnboardingApplicationHydrated) => void;
  testId?: string;
}) => {
  const { t, i18n } = useTranslation();
  const modal = useModals();

  const dataApiUrl = getEnv('VITE_FLOTIQ_ONBOARDING_APPS_CTD_URL')?.split(
    '/api/v1/content',
  )?.[0];

  const onUseTemplateClick = useCallback(() => {
    onApplicationSelect(application);
  }, [onApplicationSelect, application]);

  const openDetailsModal = useCallback(async () => {
    const useTemplate = await modal({
      title: (
        <p
          className={twMerge(
            'flex items-center h-12 md:h-16 -mx-6 md:-mx-11 px-6 md:px-8 text-xl md:text-2xl',
            'border-b border-slate-200 dark:border-slate-700 dark:text-white',
          )}
        >
          {t('PopulateSpaceModal.AppDetails.TemplateDetails')}
        </p>
      ),
      content: (
        <PopulateSpaceTemplateDetailsContent
          application={application}
          {...getTestProps(testId, 'details-modal', 'testId')}
        />
      ),
      size: '3xl',
      dialogAdditionalClasses: 'bg-slate-50',
      contentAdditionalClasses: 'pt-4 md:px-8 pb-24 md:pb-20',
    });

    if (useTemplate) {
      onApplicationSelect(application);
    }
  }, [modal, t, application, testId, onApplicationSelect]);

  return (
    <Card
      key={application.id}
      imageUrl={`${dataApiUrl}${getTranslatedField(application, 'header_image', i18n.language)?.[0]?.url}`}
      imageUrlAlt={getTranslatedField(application, 'name', i18n.language)}
      body={
        <div className="bg-white dark:bg-slate-800 p-5 flex flex-col gap-3 text-left h-full">
          <h2 className="font-bold text-xl" {...getTestProps(testId, 'name')}>
            {getTranslatedField(application, 'name', i18n.language)}
          </h2>
          <p>{getTranslatedField(application, 'excerpt', i18n.language)}</p>
          <hr className="my-3 border-slate-200 dark:border-slate-700 mt-auto" />
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={onUseTemplateClick}
              {...getTestProps(testId, 'use-template')}
            >
              {t('PopulateSpaceModal.AppDetails.UseTemplate')}
            </Button>
            <Button
              buttonColor="blueBordered"
              onClick={openDetailsModal}
              {...getTestProps(testId, 'open-details')}
            >
              {t('PopulateSpaceModal.AppDetails.ReadMore')}
            </Button>
          </div>
        </div>
      }
      imageOverlay={
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex h-full">
          <LinkButton
            link={getTranslatedField(
              application,
              'preview_link',
              i18n.language,
            )}
            target="_blank"
            rel="noreferrer"
            buttonColor="blueBordered"
            additionalClasses=" m-auto text-black focus:text-black border-0"
            iconImage={<EyeIcon />}
            iconPosition="start"
            additionalIconClasses="w-4 min-w-4"
            {...getTestProps(testId, 'live-demo')}
          >
            {t('PopulateSpaceModal.AppDetails.LiveDemo')}
          </LinkButton>
        </div>
      }
      additionalImageContainerClasses="aspect-video w-full h-auto"
      additionalContainerClasses="h-full flex flex-col"
      additionalBodyClasses="grow"
    />
  );
};

export default PopulateSpaceAppCard;
